/**
 * ANNOUNCE QUEUE (B10) — pure timing rules behind <Announcer/>. Tested in announceQueue.test.ts.
 *
 *  - Duplicates are dropped: two modules may announce the same moment (the card
 *    and useAnnouncements both speak a selection). Two messages are the same
 *    moment when their `momentKey` matches within DEDUPE_MS; the first one wins.
 *  - Polite messages that arrive within COALESCE_MS are spoken as one string,
 *    in order ("Details closed. Social infrastructure. 44 places.").
 *  - Assertive messages go out at once.
 */
export type Politeness = "polite" | "assertive";

export interface Spoken {
  text: string;
  politeness: Politeness;
}

export const DEDUPE_MS = 1500;
export const COALESCE_MS = 150;

const norm = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * What a message is about. The words before the first figure name the moment
 * ("Pune International Airport. 13 minutes…" and "Pune International Airport. 13
 * minutes off-peak…" are the same moment); a message without figures (or one that
 * starts with a figure) is keyed by its whole text. Punctuation and case are ignored.
 */
export function momentKey(text: string): string {
  const t = norm(text.trim());
  const whole = t.replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  const d = t.search(/\d/);
  if (d <= 0) return whole; // no figure, or it leads ("12 places match."): the whole text
  const head = t.slice(0, d).replace(/[^\p{L}]+/gu, "");
  return head.length >= 3 ? head : whole;
}

export class AnnounceQueue {
  private recent: { key: string; at: number }[] = [];
  private polite: string[] = [];
  private politeSince: number | null = null;
  private assertive: string[] = [];

  constructor(
    private readonly dedupeMs = DEDUPE_MS,
    private readonly coalesceMs = COALESCE_MS,
  ) {}

  /** Adds a message. false when it was dropped as a duplicate (or empty). */
  push(msg: Spoken, now: number): boolean {
    const text = msg.text.trim();
    if (!text) return false;
    const key = momentKey(text);
    this.recent = this.recent.filter((r) => now - r.at < this.dedupeMs);
    if (this.recent.some((r) => r.key === key)) return false;
    this.recent.push({ key, at: now });
    if (msg.politeness === "assertive") this.assertive.push(text);
    else {
      if (this.politeSince === null) this.politeSince = now;
      this.polite.push(text);
    }
    return true;
  }

  /** When the next flush is due (ms timestamp), or null when nothing waits. */
  due(): number | null {
    if (this.assertive.length) return 0;
    return this.politeSince === null ? null : this.politeSince + this.coalesceMs;
  }

  /** Messages ready at `now`. Assertive always; polite once the coalescing window has passed. */
  take(now: number): { polite: string | null; assertive: string | null } {
    const assertive = this.assertive.length ? this.assertive.join(" ") : null;
    this.assertive = [];
    let polite: string | null = null;
    if (this.politeSince !== null && now - this.politeSince >= this.coalesceMs) {
      polite = this.polite.join(" ");
      this.polite = [];
      this.politeSince = null;
    }
    return { polite, assertive };
  }

  clear(): void {
    this.polite = [];
    this.assertive = [];
    this.politeSince = null;
  }
}
